/**
 * lib/leaveEncashment.ts
 * ------------------------------------------------------------------
 * A REGISTER OF LEAVE DAYS SOLD BACK TO THE SCHOOL.
 *
 * Some staff have asked to be paid for leave days instead of taking them.
 * This system records ONE thing: that days were sold, by whom they were
 * recorded, and by whom they were approved — and takes those days off the
 * leave balance.
 *
 * WHAT MONEY CHANGES HANDS, HOW IT IS WORKED OUT AND WHO AUTHORISES IT ARE
 * HANDLED BY THE FINANCE OFFICE OUTSIDE THIS SYSTEM. There is deliberately
 * no amount, no rate, no payment status and no payment approval here. A
 * half-built figure in a register is worse than none: somebody would read
 * it as what the school owes.
 *
 * ONE PERSON MAY RECORD AND APPROVE. The school asked for the fact to be
 * captured, not for the system to police it — the authorisation that
 * matters happens in the finance office. So both names are always written
 * down, even when they are the same name.
 *
 * WHY A SALE IS NOT A LEAVE RECORD. The obvious shortcut was to store it
 * in hrLeave as annual leave with a flag. Seventeen screens read that
 * collection, and every one that treats an approved annual record as
 * absence would show a teacher off work on a day they were teaching. A
 * sale lives in its own collection, and only the balance engine is told
 * about it.
 *
 * WHY APPROVAL TAKES THE DAYS. Until a sale is approved it is a note of an
 * intention; once approved the days are gone, and leaving them on the
 * balance would let the same days be taken as leave as well.
 * ------------------------------------------------------------------ */
import { unpaidDaysForLeave, type EncashmentLike, type LeaveCalcOptions, type LeaveRequestLike } from '@/lib/hrLeave';
import type { HrLeaveAllocation } from '@/lib/hrPayroll';

export const ENCASHMENT_COLLECTION = 'hrLeaveEncashments';

/** Said on the screen, and the only thing this module has to say about
 *  money. Kept here so every surface words it identically. */
export const FINANCE_OFFICE_NOTE =
  'Payments and the calculation of payments are handled by the finance office '
  + 'outside this system.';

/**
 * Leave that may be sold. Annual only: sick and hospitalisation leave are
 * protection against illness rather than a benefit to be cashed, and
 * selling them would leave somebody with nothing when they are actually
 * ill.
 */
export const ENCASHABLE_LEAVE_TYPES = ['annual'] as const;
export type EncashableLeaveType = (typeof ENCASHABLE_LEAVE_TYPES)[number];

export type EncashmentStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface HrLeaveEncashment {
  id: string;
  staffId: string;
  staffName?: string;
  type: EncashableLeaveType;
  days: number;
  reason?: string;
  status: EncashmentStatus;

  /** Who recorded it. */
  requestedBy: string;
  requestedByName?: string;
  requestedAt: string;

  /** Who approved or rejected it — may be the same person who recorded it. */
  decidedBy?: string;
  decidedByName?: string;
  decidedAt?: string;
  decisionNote?: string;

  cancelledBy?: string;
  cancelledByName?: string;
  cancelledAt?: string;
  cancelReason?: string;

  createdAt?: string;
  updatedAt?: string;
}

export const ENCASHMENT_STATUS_LABEL: Record<EncashmentStatus, string> = {
  pending: 'Awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

/* ===================== the days themselves ========================= */

/** Whole or half days, and more than nothing. Leave elsewhere in the portal
 *  counts in half-day steps, and a sale must be recorded in the same unit. */
export function daysProblem(days: number): string | null {
  const d = Number(days);
  if (!Number.isFinite(d) || d <= 0) return 'Enter the number of days being sold.';
  if (Math.round(d * 2) !== d * 2) return 'Days must be in whole or half days.';
  return null;
}

/** One person's sales, in the shape the balance engine reads. Every status
 *  is passed; the engine counts only approved ones. */
export function salesFor(list: HrLeaveEncashment[] | undefined, staffId: string): EncashmentLike[] {
  return toEncashmentLike((list ?? []).filter((e) => e.staffId === staffId));
}

/** For the balance engine. */
export function toEncashmentLike(list: HrLeaveEncashment[]): EncashmentLike[] {
  return list.map((e) => ({
    type: e.type, days: e.days, status: e.status, decidedAt: e.decidedAt,
  }));
}

/* ===================== recording one =============================== */

export interface RequestCheck {
  staffStatus?: string;
  type: string;
  days: number;
  /** The balance right now, from the same engine the leave screens use. */
  available: number;
}

/**
 * Why a sale cannot be recorded, or null.
 *
 * Checked when it is RECORDED as well as when it is approved, so whoever is
 * at the desk finds out immediately.
 */
export function requestProblem(c: RequestCheck): string | null {
  if (c.staffStatus === 'exited') {
    return 'This member of staff has left. Leave owed on exit is settled through Severance, not sold.';
  }
  if (!(ENCASHABLE_LEAVE_TYPES as readonly string[]).includes(c.type)) {
    return 'Only annual leave can be sold.';
  }
  const bad = daysProblem(c.days);
  if (bad) return bad;
  if (c.days > c.available) {
    return `Only ${fmtDays(Math.max(0, c.available))} available — cannot sell ${fmtDays(c.days)}.`;
  }
  return null;
}

/* ===================== approving one =============================== */

export interface ApprovalCheck {
  encashment: HrLeaveEncashment;
  /** Balance now, excluding this sale — it is not approved yet. */
  available: number;
  /** Approved leave whose unpaid days this approval would change. */
  unpaidChanges: string[];
}

/**
 * Why a sale cannot be approved, or null.
 *
 * Note what is NOT here: nothing stops the person who recorded a sale from
 * approving it, or from approving the sale of their own leave. The school
 * asked for the names to be recorded rather than for the system to police
 * who they are, because the authorisation that releases money happens in
 * the finance office. Both names are written down either way.
 */
export function approvalProblem(c: ApprovalCheck): string | null {
  const e = c.encashment;
  if (e.status !== 'pending') {
    return `This sale is already ${ENCASHMENT_STATUS_LABEL[e.status].toLowerCase()}.`;
  }
  const bad = daysProblem(e.days);
  if (bad) return bad;
  if (e.days > c.available) {
    return `Only ${fmtDays(Math.max(0, c.available))} available now — the balance has changed `
      + `since this sale was recorded.`;
  }
  if (c.unpaidChanges.length) {
    return 'Approving this would change the unpaid leave already charged on other approved '
      + 'leave for this person. Resolve that leave first.';
  }
  return null;
}

/* ===================== undoing one ================================= */

export interface CancelCheck {
  encashment: HrLeaveEncashment;
  reason: string;
  /** Approved leave whose unpaid days cancelling would change. */
  unpaidChanges: string[];
}

export function cancelProblem(c: CancelCheck): string | null {
  const e = c.encashment;
  if (e.status !== 'pending' && e.status !== 'approved') {
    return `This sale is already ${ENCASHMENT_STATUS_LABEL[e.status].toLowerCase()}.`;
  }
  if (String(c.reason ?? '').trim().length < 3) return 'Give a reason for cancelling.';
  if (e.status === 'approved' && c.unpaidChanges.length) {
    return 'Cancelling would change the unpaid leave already charged on other approved leave '
      + 'for this person. Resolve that leave first.';
  }
  return null;
}

/* ===================== the unpaid-leave knock-on ================== */

/**
 * Which approved leave would be charged differently if the sales changed
 * from `before` to `after`?
 *
 * This is about the school's existing UNPAID LEAVE rule, not about paying
 * for a sale. Approving leave beyond the balance writes a deduction for the
 * days that were not covered. A sale moves that balance, so cancelling one
 * can leave somebody docked for days they now have, and approving one could
 * make earlier leave unpaid after the fact — in a month that may already
 * have been paid. Returned so the caller refuses, never so it rewrites.
 */
export function unpaidChanges(
  staffLeave: ({ id: string } & LeaveRequestLike)[],
  allocation: HrLeaveAllocation | undefined,
  before: EncashmentLike[],
  after: EncashmentLike[],
  /** The person's accrual basis (lib/leavePolicy leaveBasis). */
  opts?: LeaveCalcOptions,
): string[] {
  const changed: string[] = [];
  for (const l of staffLeave) {
    if (l.status !== 'approved' || l.type === 'unpaid') continue;
    const a = unpaidDaysForLeave(l, allocation, staffLeave, before, opts);
    const b = unpaidDaysForLeave(l, allocation, staffLeave, after, opts);
    if (Math.abs(a - b) > 0.001) changed.push(l.id);
  }
  return changed;
}

/** "1 day", "2.5 days". */
export function fmtDays(n: number): string {
  const v = Math.round(n * 100) / 100;
  return `${v} day${v === 1 ? '' : 's'}`;
}
